import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { InstructorCheckInDTO } from "../../domain/repo/list-instructor-check-ins";

type InstructorAttendanceRecordsTableProps = {
  data: InstructorCheckInDTO[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
};

export function InstructorAttendanceRecordsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
}: InstructorAttendanceRecordsTableProps) {
  const columns: TanstackReactTable.ColumnDef<InstructorCheckInDTO>[] = [
    createSortableColumn("checkedInAt", "ວັນທີ & ເວລາ", {
      cell: ({ row }) => (
        <div>
          <div className="font-semibold">
            {formatDate(row.original.checkedInAt)}
          </div>
          <div className="text-muted-foreground text-xs">
            {formatDateTime(row.original.checkedInAt)}
          </div>
        </div>
      ),
    }),
    {
      header: "ຄູສອນ",
      accessorKey: "instructorName",
      cell: ({ row }) => (
        <Link
          to="/app/master/instructors/$id/edit"
          params={{ id: row.original.instructorId }}
          className="font-medium text-primary hover:underline"
        >
          {row.original.instructorName}
        </Link>
      ),
    },
    {
      header: "ປະເພດ",
      accessorKey: "sessionType",
      cell: ({ row }) => (
        <Badge variant="outline">
          {row.original.sessionType === "general_class"
            ? "ຄລາດສ໌ທົ່ວໄປ"
            : "ຮຽນສ່ວນຕົວ"}
        </Badge>
      ),
    },
    {
      header: "ຄລາດສ໌ / ການຈອງ",
      accessorKey: "className",
      cell: ({ row }) => {
        const r = row.original;
        const isPrivateLesson = r.sessionType === "private_lesson";
        if (isPrivateLesson) {
          return (
            <div>
              {r.privateLessonDate && (
                <div className="font-semibold">
                  {formatDate(r.privateLessonDate)}
                </div>
              )}
              {r.privateLessonStartTime && r.privateLessonEndTime && (
                <div className="text-muted-foreground text-xs">
                  {r.privateLessonStartTime} - {r.privateLessonEndTime}
                </div>
              )}
              {r.studentName && (
                <div className="text-muted-foreground text-xs">
                  {r.studentName}
                </div>
              )}
              {!r.privateLessonDate && !r.privateLessonStartTime && "-"}
            </div>
          );
        }
        return (
          <Link
            to="/app/classes/sessions/$id"
            params={{ id: r.classSessionId ?? "" }}
            className="font-semibold text-primary hover:underline"
          >
            {r.className ?? "-"}
          </Link>
        );
      },
    },
    {
      header: "ຫ້ອງ",
      accessorKey: "roomName",
      cell: ({ row }) => row.original.roomName ?? "-",
    },
    {
      header: "ຜູ້ກວດຊື່",
      accessorKey: "checkedInByName",
      cell: ({ row }) => row.original.checkedInByName ?? "-",
    },
  ];

  return (
    <DataTable<InstructorCheckInDTO, unknown>
      noDataMessage="ບໍ່ພົບບັນທຶກການກວດຊື່ຄູສອນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
